import { createRouter, createWebHistory } from "vue-router";
import type { RouteRecordRaw } from "vue-router";
import CompetencyManagerView from "../views/CompetencyManager.vue";
import KanbanBoardView from "../views/KanbanBoardView.vue";
import SurveyContainer from "../components/SurveyContainer.vue";
import MobileView from "../views/MobileView.vue";
import ThankYouStage from "../components/survey/ThankYouStage.vue";
import ContactInfoStage from "../components/survey/ContactInfoStage.vue";
import ResidencyWorkArrangementsStage from "../components/survey/ResidencyWorkArrangementsStage.vue";
import GameSurvey from "../components/GameSurvey.vue";
import ProfessionalExperienceStage from "../components/survey/ProfessionalExperienceStage.vue";
import CareerPathwaysStage from "../components/survey/CareerPathwaysStage.vue";
import DemographicsStage from "../components/survey/DemographicsStage.vue";
import EmailAuth from "../components/EmailAuth.vue";
import SurveyAdmin from "../views/SurveyAdmin.vue";
import ConsentRequired from "../views/ConsentRequired.vue";
import SurveyInfo from "../views/SurveyInfo.vue";
import KanbanInstructionsView from "../views/KanbanInstructionsView.vue";
import { auth } from "@/config/firebase";

// Add this to fix TS error for custom window property
declare global {
  interface Window {
    __SURVEY_TEST_MODE__?: boolean;
  }
}

const routes: RouteRecordRaw[] = [
  {
    path: "/",
    redirect: "/survey/contact-info",
  },
  {
    path: "/auth",
    name: "Auth",
    component: EmailAuth,
  },
  {
    path: "/surveyadmin",
    name: "SurveyAdmin",
    component: SurveyAdmin,
  },
  {
    path: "/survey",
    name: "Survey",
    component: SurveyContainer,
    children: [
      // add default rout for survey
      {
        path: "",
        redirect: "/survey/contact-info",
      },
      {
        path: "contact-info",
        name: "ContactInfo",
        component: ContactInfoStage,
      },
      {
        path: "residency-work-arrangements",
        name: "ResidencyWorkArrangements",
        component: ResidencyWorkArrangementsStage,
      },
      {
        path: "competencies",
        name: "GameSurvey",
        component: GameSurvey,
      },
      {
        path: "experience",
        name: "ProfessionalExperience",
        component: ProfessionalExperienceStage,
      },
      {
        path: "career-pathways",
        name: "CareerPathways",
        component: CareerPathwaysStage,
      },
      {
        path: "demographics",
        name: "Demographics",
        component: DemographicsStage,
      },
      {
        path: "thank-you",
        name: "ThankYouStage",
        component: ThankYouStage,
      },
    ],
  },
  {
    path: "/survey-info",
    name: "SurveyInfo",
    component: SurveyInfo,
  },
  {
    path: "/competency-manager",
    name: "CompetencyManager",
    component: CompetencyManagerView,
  },
  {
    path: "/kanban-board",
    name: "KanbanBoard",
    component: KanbanBoardView,
  },
  {
    path: "/mobile",
    name: "MobileView",
    component: MobileView,
  },
  {
    path: "/consent-required",
    name: "ConsentRequired",
    component: ConsentRequired,
  },
  {
    path: "/kanban-instructions",
    name: "KanbanInstructions",
    component: KanbanInstructionsView,
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/",
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    // Always scroll to top when entering a route
    return { top: 0 };
  },
});

// Route guard to prevent access to survey stages without authentication and consent
router.beforeEach(async (to, from, next) => {
  // Always allow access to the survey info page and kanban instructions
  if (to.path === "/survey-info" || to.path === "/kanban-instructions") {
    next();
    return;
  }
  // Allow all survey navigation in test mode
  if (typeof window !== "undefined" && window.__SURVEY_TEST_MODE__) {
    next();
    return;
  }

  // Check if the route is a survey stage
  if (to.path.startsWith("/survey/")) {
    const currentUser = auth.currentUser;

    // Allow access to contact-info stage without authentication or consent
    if (to.path === "/survey/contact-info") {
      next();
      return;
    }

    // Check for consent first (except in test mode)
    const hasConsent = localStorage.getItem("surveyConsent");
    if (!hasConsent && !window.__SURVEY_TEST_MODE__) {
      console.log("Router: Blocking access to survey - consent not given");
      console.log("Attempted route:", to.path);
      // Allow access but forms will be disabled
      next();
      return;
    }

    // Require authentication for all other survey stages
    if (!currentUser) {
      console.log(
        "Router: Blocking access to survey stage - user not authenticated"
      );
      console.log("Attempted route:", to.path);
      console.log("Firebase auth state:", currentUser);
      // Redirect to the first survey stage
      next("/survey/contact-info");
      return;
    }

    // Prevent navigation back from thank-you stage (unless in test mode)
    if (
      from.name === "ThankYouStage" &&
      to.name !== "ThankYouStage" &&
      !window.__SURVEY_TEST_MODE__
    ) {
      console.log("Router: Blocking navigation back from Thank You stage");
      next(false);
      return;
    }

    // Prevent access to previous stages if user has completed the survey
    // Check if user has completed the survey by looking for survey completion flag
    const hasCompletedSurvey = localStorage.getItem("surveyCompleted");
    if (hasCompletedSurvey === "true" && to.name !== "ThankYouStage") {
      console.log(
        "Router: User has completed survey, redirecting to Thank You stage"
      );
      next("/survey/thank-you");
      return;
    }
  }

  // Check if the route is the thank-you stage
  if (to.name === "ThankYouStage") {
    // Check if user is authenticated
    const currentUser = auth.currentUser;
    if (!currentUser) {
      console.log(
        "Router: Blocking access to thank-you stage - user not authenticated"
      );
      console.log("Firebase auth state:", currentUser);
      // Redirect to the first survey stage
      next("/survey/contact-info");
      return;
    }
  }
  next();
});

export default router;
